import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import MergeTicketModal from "../MergeTicketModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [mergeModalOpen, setMergeModalOpen] = useState(false);
	const [archiving, setArchiving] = useState(false);
	const [pinning, setPinning] = useState(false);
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
	const [muting, setMuting] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleArchive = async () => {
		handleClose();
		setArchiving(true);
		try {
			await api.post(`/messages/${ticket.id}/archive`, { archive: true });
			toast.success("Conversa arquivada no WhatsApp.");
		} catch (err) {
			toastError(err);
		} finally {
			setArchiving(false);
		}
	};

	const handlePinChat = async () => {
		handleClose();
		setPinning(true);
		try {
			await api.post(`/messages/${ticket.id}/pin`, { pin: true });
			toast.success("Conversa fixada no WhatsApp.");
		} catch (err) {
			toastError(err);
		} finally {
			setPinning(false);
		}
	};

	const handleClearChat = async () => {
		setClearConfirmOpen(false);
		try {
			await api.delete(`/messages/${ticket.id}/history`);
			toast.success("Histórico apagado no WhatsApp.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleMuteGroup = async () => {
		handleClose();
		setMuting(true);
		try {
			await api.post(`/contacts/${ticket.contactId}/mute`, { muted: true });
			toast.success("Grupo mutado — não vai mais aparecer na lista geral.");
		} catch (err) {
			toastError(err);
		} finally {
			setMuting(false);
		}
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				{ticket.status === "open" && (
					<MenuItem onClick={handleOpenTransferModal}>
						{i18n.t("ticketOptionsMenu.transfer")}
					</MenuItem>
				)}
				{ticket.status === "open" && (
					<MenuItem onClick={() => { setMergeModalOpen(true); handleClose(); }}>
						Mesclar com outro ticket
					</MenuItem>
				)}
				{ticket.isGroup && !ticket.contact?.muted && (
					<MenuItem onClick={handleMuteGroup} disabled={muting}>
						Mutar grupo (sumir da lista geral) 🔇
					</MenuItem>
				)}
				{ticket.whatsappId && (
					<MenuItem onClick={handleArchive} disabled={archiving}>
						Arquivar conversa no WA
					</MenuItem>
				)}
				{ticket.whatsappId && (
					<MenuItem onClick={handlePinChat} disabled={pinning}>
						Fixar conversa no WA 📌
					</MenuItem>
				)}
				{ticket.whatsappId && (
					<MenuItem onClick={() => { handleClose(); setClearConfirmOpen(true); }} style={{ color: "#d32f2f" }}>
						Apagar histórico no WA
					</MenuItem>
				)}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal} style={{ color: "#d32f2f" }}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket?.contact?.name || ticket?.contact?.number || ""
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
				ticketUserId={ticket.userId}
			/>
			<MergeTicketModal
				open={mergeModalOpen}
				onClose={() => setMergeModalOpen(false)}
				ticketId={ticket.id}
			/>
			<ConfirmationModal
				title="Apagar histórico do WhatsApp?"
				open={clearConfirmOpen}
				onClose={setClearConfirmOpen}
				onConfirm={handleClearChat}
			>
				Esta ação apaga o histórico de mensagens desta conversa no WhatsApp. Não pode ser desfeita.
			</ConfirmationModal>
		</>
	);
};

export default TicketOptionsMenu;
